import { Colors } from '@factorialco/deprecated-design-system'
import {
  BudgetOwnerFragment,
  FinanceValueObjectsBudgetPeriodicityEnum,
  FinanceValueObjectsBudgetStatusEnum,
} from 'generated/resources/graphql'

import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'

export const amountWithCurrency = (amount: number, currency: string, locale: string) => {
  return MoneyWithCurrency({
    optionalDecimals: true,
    amount: isNaN(amount) ? 0 : amount,
    currency,
    locale,
  })
}

export const formatPeriod = (
  date: moment.Moment,
  periodicity: FinanceValueObjectsBudgetPeriodicityEnum
) => {
  switch (periodicity) {
    case 'daily':
      return date.format('MMMM DD, YYYY')
    case 'weekly':
      return i18n.t('budgets.detail.period_status.week_of_year', { week: date.format('W') })
    case 'monthly':
      return date.format('MMMM YYYY')
    default:
      return date.format('YYYY')
  }
}

export const avatarsForOwners = (owners: BudgetOwnerFragment[]) => {
  return owners.map((owner) => {
    return {
      label: owner.fullName,
      url: buildUrl(owner?.avatar?.url),
    }
  })
}

export const periodicityOptions = () => [
  {
    value: 'none',
    label: i18n.t('budgets.forms.new_budget.budget_limits.periodicity.none'),
  },
  {
    value: 'daily',
    label: i18n.t('budgets.forms.new_budget.budget_limits.periodicity.daily'),
  },
  {
    value: 'weekly',
    label: i18n.t('budgets.forms.new_budget.budget_limits.periodicity.weekly'),
  },
  {
    value: 'monthly',
    label: i18n.t('budgets.forms.new_budget.budget_limits.periodicity.monthly'),
  },
  {
    value: 'yearly',
    label: i18n.t('budgets.forms.new_budget.budget_limits.periodicity.yearly'),
  },
]
export const budgetPeriodicity = () =>
  ({
    ['daily']: i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.day'),
    ['weekly']: i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.week'),
    ['monthly']: i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.month'),
    ['yearly']: i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.year'),
    ['none']: i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.none'),
  } as const)

export const limitLabelForSummary = (periodicity: string) => {
  switch (periodicity) {
    case 'daily':
      return i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.day')
    case 'weekly':
      return i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.week')
    case 'monthly':
      return i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.month')
    case 'yearly':
      return i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.year')
    case 'none':
      return i18n.t('budgets.forms.new_budget.budget_limits.budget_periodicity.none')
    default:
      throw new Error(`Unhandled budget periodicity`)
  }
}

interface BudgetProgress {
  barColor: Colors
  progressColor: Colors
}
export const budgetProgress = (status: FinanceValueObjectsBudgetStatusEnum): BudgetProgress => {
  switch (status) {
    case 'over_budget':
      return {
        barColor: 'radicalRed200',
        progressColor: 'radicalRed1000',
      }
    case 'budget_risk':
      return {
        barColor: 'champagne200',
        progressColor: 'champagne1000',
      }
    case 'within_budget':
      return {
        barColor: 'viridian200',
        progressColor: 'viridian1000',
      }
    default:
      const exhaustiveCheck: never = status
      throw new Error(`Unhandled budget status: ${exhaustiveCheck}`)
  }
}
